import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axiosServices from '../../api/interceptor';
import moment from 'moment';

const ProbationEvaluationList = () => {
  const { user } = useSelector((state) => state.tokens);
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [evalToDelete, setEvalToDelete] = useState(null);

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const response = await axiosServices.get('/evaluations');
      if (response.data && response.data.success) {
        setEvaluations(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch evaluations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchEvaluations();
    }
  }, [user]);

  const handleDelete = async () => {
    if (!evalToDelete) return;
    try {
      setLoading(true);
      await axiosServices.delete(`/evaluations/${evalToDelete}`);
      setDeleteOpen(false);
      setEvalToDelete(null);
      fetchEvaluations();
    } catch (err) {
      console.error(err);
      setError('Failed to delete evaluation.');
      setLoading(false);
    }
  };

  const isAdminOrHR = user?.permission === "Admin" || user?.permission === "Admin-a" || user?.permission === "HR Management";

  if (!user || !isAdminOrHR) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>You are not authorized to view this page. Only Admins and HR Management can access the list.</Alert>
      </Container>
    );
  }

  const getRecommendationColor = (recommendation) => {
    if (recommendation.includes('Confirm employment')) return 'success';
    if (recommendation.includes('Extend')) return 'warning';
    return 'error';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: '#203864' }}>
          Probation Evaluations List
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          View previously submitted probation period evaluations
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Employee Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Position / Dept</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Evaluation Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Evaluator</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Recommendation</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', align: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {evaluations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No evaluations found.
                    </TableCell>
                  </TableRow>
                ) : (
                  evaluations.map((evalItem) => (
                    <TableRow key={evalItem._id} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight={500}>{evalItem.employeeName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{evalItem.positionRole}</Typography>
                        <Typography variant="caption" color="textSecondary">{evalItem.departmentTeam}</Typography>
                      </TableCell>
                      <TableCell>
                        {evalItem.dateOfEvaluation ? moment(evalItem.dateOfEvaluation, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('DD MMM YYYY') : '--'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{evalItem.evaluatorNameTitle}</Typography>
                      </TableCell>
                      <TableCell>
                        {evalItem.probationRecommendation?.recommendation ? (
                           <Chip 
                             label={evalItem.probationRecommendation.recommendation} 
                             size="small" 
                             color={getRecommendationColor(evalItem.probationRecommendation.recommendation)} 
                             variant="outlined"
                             sx={{ fontWeight: 'bold' }}
                           />
                        ) : '--'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            color="primary" 
                            onClick={() => navigate(`/hr-management/probation-evaluation/view/${evalItem._id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            color="error" 
                            onClick={() => { setEvalToDelete(evalItem._id); setDeleteOpen(true); }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      >
        <DialogTitle>Delete Evaluation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this evaluation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProbationEvaluationList;
